import { useState, useRef } from 'preact/hooks'
import { apiClient, InitData } from '../../core'
import { t } from '../../core/i18n'

interface Props {
  initData: InitData
  title?: string | null
  subtitle?: string | null
  showPhone?: boolean
  /** Sat når formularen åbnes fra en «Kontakt mig»-plan i pris-widget'en (OPG-0063):
   *  serveren slår planens navn op og bruger det som emne, som den native modal gør. */
  planId?: string | null
}

/**
 * ContactFormWidget — mirrors HS-native form styling.
 *
 * - Inputs: 1px border, 0.75rem 1rem padding, focus ring in --yoga-primary
 * - Textarea: min-height 120px, vertical resize
 * - Submit: filled orange (--yoga-primary), white text
 * - Success: green checkmark + tak-besked
 * - Error: red border on inputs (via .hsw-has-error) + inline message
 * - Loading: button disabled with "Sender..."
 *
 * initData is currently unused by the form fields — the widget posts to
 * /api/widget/contact which already resolves the tenant from the request
 * context. _initData prefix silences unused-vars lint without disabling.
 */
export function ContactFormWidget({ initData: _initData, title = null, subtitle = null, showPhone = true, planId = null }: Props) {
  const str = t(_initData.locale)
  const heading = title || str.contactTitle
  const sub = subtitle || str.contactFormIntro
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot — bots fill this; humans don't
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ name?: boolean; email?: boolean; message?: boolean }>({})
  // Tidspunktet beskedfeltet første gang fik fokus. /api/widget/contact læser
  // composeMs (tiden brugt på beskeden) i spamtjekket (lib/spam-check.ts), som
  // HS' egne kontaktformularer. Widgetten sendte formStartedAt, som ruten aldrig
  // læste, så tjekket kørte ikke for widgetten (tog 7 #8).
  const messageFocusedAtRef = useRef<number | null>(null)

  async function handleSubmit(e: Event) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)

    const nextFieldErrors: { name?: boolean; email?: boolean; message?: boolean } = {}
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedMessage = message.trim()
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!trimmedName) nextFieldErrors.name = true
    if (!trimmedEmail || !emailPattern.test(trimmedEmail)) nextFieldErrors.email = true
    if (!trimmedMessage) nextFieldErrors.message = true

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors)
      setError(str.fillMarkedFields)
      setSubmitting(false)
      return
    }

    setFieldErrors({})

    const result = await apiClient.post('/api/widget/contact', {
      name,
      email,
      phone: phone || null,
      message,
      website,
      composeMs: messageFocusedAtRef.current != null ? Date.now() - messageFocusedAtRef.current : undefined,
      ...(planId ? { planId } : {}),
    })

    setSubmitting(false)

    if (result.error) {
      setError(result.error)
    } else {
      setSubmitted(true)
    }
  }

  // "Modtag kontaktforespørgsler" er slået fra: /api/widget/contact ville afvise
  // indsendelsen, så formularen vises slet ikke (samme adfærd som /kontakt).
  if (_initData.features?.contactRequestsEnabled === false) return null

  if (submitted) {
    return (
      <div>
        <style>{contactStyles}</style>
        <div class="hsw-contact-card hsw-success">
          <div class="hsw-success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 class="hsw-success-title">{str.contactFormThanks}</h3>
          <p class="hsw-success-text">{str.contactFormSuccess}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <style>{contactStyles}</style>
      <div class="hsw-contact-card">
        <h2 class="hsw-contact-title">{heading}</h2>
        <p class="hsw-contact-subtitle">{sub}</p>

        <form class="hsw-contact-form" onSubmit={handleSubmit} noValidate>
          {/* Honeypot — visually hidden; bots auto-fill it, humans skip it */}
          <div class="hsw-honeypot" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autocomplete="off"
                value={website}
                onInput={(e) => setWebsite((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>
          <div class="hsw-row">
            <div class="hsw-field">
              <label class="hsw-label" for="hsw-cf-name">{str.fieldName}</label>
              <input
                id="hsw-cf-name"
                class={`hsw-input${fieldErrors.name ? ' hsw-has-error' : ''}`}
                type="text"
                value={name}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value
                  setName(v)
                  if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: false }))
                  if (error) setError(null)
                }}
                required
                autocomplete="name"
              />
            </div>
            <div class="hsw-field">
              <label class="hsw-label" for="hsw-cf-email">{str.fieldEmail}</label>
              <input
                id="hsw-cf-email"
                class={`hsw-input${fieldErrors.email ? ' hsw-has-error' : ''}`}
                type="email"
                value={email}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value
                  setEmail(v)
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: false }))
                  if (error) setError(null)
                }}
                required
                autocomplete="email"
              />
            </div>
          </div>

          {showPhone && (
            <div class="hsw-field">
              <label class="hsw-label" for="hsw-cf-phone">
                {str.fieldPhone} <span class="hsw-optional">{str.optionalSuffix}</span>
              </label>
              <input
                id="hsw-cf-phone"
                class="hsw-input"
                type="tel"
                value={phone}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value
                  setPhone(v)
                  if (error) setError(null)
                }}
                autocomplete="tel"
              />
            </div>
          )}

          <div class="hsw-field">
            <label class="hsw-label" for="hsw-cf-message">{str.fieldMessage}</label>
            <textarea
              id="hsw-cf-message"
              onFocus={() => { if (messageFocusedAtRef.current == null) messageFocusedAtRef.current = Date.now() }}
              class={`hsw-input hsw-textarea${fieldErrors.message ? ' hsw-has-error' : ''}`}
              value={message}
              onInput={(e) => {
                const v = (e.target as HTMLTextAreaElement).value
                setMessage(v)
                if (fieldErrors.message) setFieldErrors((prev) => ({ ...prev, message: false }))
                if (error) setError(null)
              }}
              required
            />
          </div>

          {error && (
            <div class="hsw-error" role="alert">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            class="hsw-submit"
            disabled={submitting}
          >
            {submitting ? str.sending : str.sendMessage}
          </button>
        </form>
      </div>
    </div>
  )
}

const contactStyles = `
  .hsw-contact-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2.5rem 2rem;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-contact-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    margin: 0 0 0.4rem;
    letter-spacing: -0.01em;
  }
  .hsw-contact-subtitle {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0 0 1.75rem;
    line-height: 1.5;
  }
  .hsw-contact-form {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
  }
  /* Off-screen honeypot — must remain focusable-out-of-tab-order but invisible */
  .hsw-honeypot {
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
  .hsw-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.1rem;
  }
  @media (max-width: 540px) {
    .hsw-row { grid-template-columns: 1fr; }
    .hsw-contact-card { padding: 2rem 1.25rem; }
  }
  .hsw-field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .hsw-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: 0.01em;
  }
  .hsw-optional {
    font-weight: 400;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-input {
    width: 100%;
    box-sizing: border-box;
    background: var(--yoga-card-bg, #ffffff);
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0);
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    font-family: inherit;
    line-height: 1.4;
    transition: border-color .2s, box-shadow .2s;
    outline: none;
  }
  .hsw-input::placeholder {
    color: var(--yoga-text-muted, #a3a3a3);
  }
  .hsw-input:hover {
    border-color: rgba(0,0,0,0.25);
  }
  .hsw-input:focus {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 0 0 3px rgba(255,107,53,0.15);
  }
  .hsw-input.hsw-has-error {
    border-color: #dc2626;
  }
  .hsw-input.hsw-has-error:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220,38,38,0.15);
  }
  .hsw-textarea {
    min-height: 120px;
    resize: vertical;
  }
  .hsw-error {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #dc2626;
    background: rgba(220,38,38,0.07);
    border: 1px solid rgba(220,38,38,0.25);
    border-radius: var(--yoga-radius, 0);
    padding: 0.65rem 0.85rem;
    font-size: 0.875rem;
    line-height: 1.4;
  }
  .hsw-submit {
    margin-top: 0.25rem;
    background: var(--yoga-primary, #FF6B35);
    color: #ffffff;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0);
    padding: 0.9rem 2rem;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background .2s, border-color .2s, transform .1s;
    align-self: flex-start;
  }
  .hsw-submit:hover:not(:disabled) {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-submit:active:not(:disabled) {
    transform: translateY(1px);
  }
  .hsw-submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  @media (max-width: 540px) {
    .hsw-submit { align-self: stretch; }
  }
  .hsw-contact-card.hsw-success {
    text-align: center;
    padding: 3rem 2rem;
  }
  .hsw-success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 999px;
    background: rgba(22,163,74,0.12);
    color: #16a34a;
    margin: 0 auto 1.25rem;
  }
  .hsw-success-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    margin: 0 0 0.5rem;
    letter-spacing: -0.01em;
  }
  .hsw-success-text {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0;
    line-height: 1.5;
  }
`
